import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";

const API_URL = process.env.REACT_APP_API_URL;

const ManageEmployees = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { activeCompany, isDemo } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [sort, setSort] = useState({ key: "first_name", dir: "asc" });

  const [newEmployee, setNewEmployee] = useState({
    first_name: "",
    last_name: "",
    address: "",
    wage: "",
    contact_info: { email: "", phone: "" },
    is_salary: false,
    salary_period: "weekly",
  });

  useEffect(() => {

    const fetchEmployees = async () => {
      if (!auth || !auth.isAuthenticated || !activeCompany) { setLoading(false); return; }

      try {
        const token = auth.user?.access_token;
        const response = await fetch(`${API_URL}/employees?company_id=${activeCompany.company_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          setError("Failed to fetch employees.");
          return;
        }

        const data = await response.json();
        setEmployees(data);
      } catch (err) {
        setError("An error occurred while fetching employees.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, activeCompany]);


  const handleAddEmployee = async () => {

    const token = auth.user?.access_token;
    if (!token) {
      console.error("No authentication token found.");
      return;
    }

    const trimmedFirstName = newEmployee.first_name.trim();
    const trimmedLastName = newEmployee.last_name.trim();

    if (!trimmedFirstName || !trimmedLastName) {
      alert(t("employee_required"));
      return;
    }

    const employeeData = {
      company_id: activeCompany?.company_id || null,
      first_name: trimmedFirstName,
      last_name: trimmedLastName,
      address: newEmployee.address.trim() || "",
      wage: newEmployee.wage !== "" ? Number(newEmployee.wage) : 0.0,
      contact_info: newEmployee.contact_info || { phone: "", email: "" },
      is_salary: newEmployee.is_salary,
      salary_period: newEmployee.is_salary ? newEmployee.salary_period : "weekly",
    };

    try {
      const response = await fetch(`${API_URL}/employees`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(employeeData),
      });

      if (!response.ok) {
        if (response.status === 403) {
          const body = await response.json().catch(() => ({}));
          if (body?.detail?.error === "trial_limit_exceeded") {
            alert("You've reached the 4-employee limit for trial accounts. Subscribe to add more.");
            return;
          }
        }
        throw new Error(`Failed to add employee: ${response.status}`);
      }

      const result = await response.json();
      setEmployees([...employees, result.employee]);
      setShowAddModal(false);
      setNewEmployee({
        first_name: "",
        last_name: "",
        address: "",
        wage: "",
        contact_info: { email: "", phone: "" },
        is_salary: false,
        salary_period: "weekly",
      });
    } catch (error) {
      console.error("Error adding employee:", error);
    }
  };

  const handleEditClick = (employee) => {

    setSelectedEmployee({
      ...employee,
      contact_info: employee.contact_info || { email: "", phone: "" },
      is_salary: employee.is_salary || false,
      salary_period: employee.salary_period || "weekly",
    });
    setShowEditModal(true);
  };

  const handleEditEmployee = async () => {
    const token = auth.user?.access_token;
    if (!token) {
      console.error("No authentication token found.");
      return;
    }

    const updatedEmployee = {
      first_name: selectedEmployee.first_name,
      last_name: selectedEmployee.last_name,
      address: selectedEmployee.address || "",
      wage: selectedEmployee.wage ?? null,
      contact_info: selectedEmployee.contact_info || { phone: "", email: "" },
      is_salary: selectedEmployee.is_salary || false,
      salary_period: selectedEmployee.is_salary ? (selectedEmployee.salary_period || "weekly") : "weekly",
    };

    try {
      const response = await fetch(`${API_URL}/employees/${selectedEmployee.employee_id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedEmployee),
      });

      if (!response.ok) {
        throw new Error(`Failed to update employee: ${response.status}`);
      }

      setEmployees((prevEmployees) =>
        prevEmployees.map((emp) =>
          emp.employee_id === selectedEmployee.employee_id ? { ...emp, ...updatedEmployee } : emp
        )
      );

      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating employee:", error);
    }
  };

  const handleDeleteEmployee = async (employee_id) => {

    const confirmDelete = window.confirm(t("delete_employee_confirm"));
    if (!confirmDelete) return;

    const token = auth.user?.access_token;
    if (!token) {
      console.error("No authentication token found.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/employees/${employee_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete employee: ${response.status}`);
      }

      setEmployees((prevEmployees) => prevEmployees.filter((emp) => emp.employee_id !== employee_id));
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };


  const toggleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedEmployees = useMemo(() => {
    const { key, dir } = sort;
    return [...employees].sort((a, b) => {
      let av, bv;
      if (key === "email") {
        av = (a.contact_info?.email || "").toLowerCase();
        bv = (b.contact_info?.email || "").toLowerCase();
      } else if (key === "phone") {
        av = (a.contact_info?.phone || "").toLowerCase();
        bv = (b.contact_info?.phone || "").toLowerCase();
      } else if (key === "wage") {
        return dir === "asc" ? (Number(a.wage) || 0) - (Number(b.wage) || 0) : (Number(b.wage) || 0) - (Number(a.wage) || 0);
      } else {
        av = (a[key] || "").toLowerCase();
        bv = (b[key] || "").toLowerCase();
      }
      return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [employees, sort]);

  const sortIcon = (col) => (
    <span className={`ml-1 text-xs ${sort.key === col ? "text-gray-700" : "text-gray-300"}`}>
      {sort.key === col ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const wageLabel = (is_salary, period) => {
    if (!is_salary) return "Hourly Wage ($)";
    return period === "yearly" ? "Annual Salary ($)" : period === "monthly" ? "Monthly Salary ($)" : "Weekly Salary ($)";
  };

  const wageDisplay = (emp) => {
    if (!emp.is_salary) return `$${Number(emp.wage).toFixed(2)}/hr`;
    const suffix = emp.salary_period === "yearly" ? "/yr" : emp.salary_period === "monthly" ? "/mo" : "/wk";
    return `$${Number(emp.wage).toFixed(2)}${suffix}`;
  };

  const SalaryPeriodSelector = ({ value, onChange }) => (
    <div className="flex gap-1 mb-2">
      {["weekly", "monthly", "yearly"].map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={`flex-1 py-1.5 text-xs font-medium rounded border transition ${
            value === p
              ? "bg-green-500 text-white border-green-500"
              : "bg-white text-gray-600 border-gray-300 hover:border-green-400"
          }`}
        >
          {p.charAt(0).toUpperCase() + p.slice(1)}
        </button>
      ))}
    </div>
  );

  const handleSignOut = () => {
    auth.signOut();
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex flex-col items-center justify-center flex-1 p-3 sm:p-6">
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full max-w-5xl">

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">{t("manage_employees_title")}</h1>
            {isDemo && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">
                {employees.length}/4 trial
              </span>
            )}
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={isDemo && employees.length >= 4}
            title={isDemo && employees.length >= 4 ? "Subscribe to add more employees" : undefined}
            className={`px-4 py-2 rounded-lg transition ${isDemo && employees.length >= 4 ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-green-500 text-white hover:bg-green-600"}`}
          >
            {t("add_employee_btn")}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-gray-400 text-sm">Loading employees…</span>
          </div>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-200 text-gray-700">
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("first_name")}>
                      {t("col_first_name")}{sortIcon("first_name")}
                    </th>
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("last_name")}>
                      {t("col_last_name")}{sortIcon("last_name")}
                    </th>
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("address")}>
                      {t("col_address")}{sortIcon("address")}
                    </th>
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("email")}>
                      {t("col_email")}{sortIcon("email")}
                    </th>
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("phone")}>
                      {t("col_phone")}{sortIcon("phone")}
                    </th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("wage")}>
                      {t("col_wage")}{sortIcon("wage")}
                    </th>
                    <th className="px-4 py-2 border text-center">{t("col_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEmployees.map((employee) => (
                    <tr key={employee.employee_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border font-medium">{employee.first_name}</td>
                      <td className="px-4 py-2 border">{employee.last_name}</td>
                      <td className="px-4 py-2 border text-gray-500">{employee.address || "—"}</td>
                      <td className="px-4 py-2 border text-gray-500">{employee.contact_info?.email || "—"}</td>
                      <td className="px-4 py-2 border text-gray-500">{employee.contact_info?.phone || "—"}</td>
                      <td className="px-4 py-2 border text-right tabular-nums">{wageDisplay(employee)}</td>
                      <td className="px-4 py-2 border text-center whitespace-nowrap">
                        <button onClick={() => handleEditClick(employee)} className="px-3 py-1 rounded text-xs bg-blue-500 text-white hover:bg-blue-600 transition mr-2">{t("edit")}</button>
                        <button onClick={() => handleDeleteEmployee(employee.employee_id)} className="px-3 py-1 rounded text-xs bg-red-500 text-white hover:bg-red-600 transition">{t("delete")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {sortedEmployees.map((employee) => (
                <div key={employee.employee_id} className="border border-gray-200 rounded-lg p-3 bg-white">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <p className="font-semibold text-gray-800">{employee.first_name} {employee.last_name}</p>
                      <p className="text-sm font-medium text-gray-600">{wageDisplay(employee)}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => handleEditClick(employee)} className="px-3 py-1 rounded text-xs bg-blue-500 text-white hover:bg-blue-600 transition">{t("edit")}</button>
                      <button onClick={() => handleDeleteEmployee(employee.employee_id)} className="px-3 py-1 rounded text-xs bg-red-500 text-white hover:bg-red-600 transition">{t("delete")}</button>
                    </div>
                  </div>
                  {employee.address && <p className="text-xs text-gray-500 mt-1">{employee.address}</p>}
                  <div className="flex gap-4 mt-1">
                    {employee.contact_info?.email && <p className="text-xs text-gray-500">{employee.contact_info.email}</p>}
                    {employee.contact_info?.phone && <p className="text-xs text-gray-500">{employee.contact_info.phone}</p>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <Link to="/">
          <button className="mt-6 w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition">
            {t("back_to_dashboard_btn")}
          </button>
        </Link>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">{t("add_employee_title")}</h2>

            <input
              type="text"
              placeholder={t("col_first_name")}
              className="w-full p-2 border rounded mb-2"
              value={newEmployee.first_name}
              onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
            />
            <input
              type="text"
              placeholder={t("col_last_name")}
              className="w-full p-2 border rounded mb-2"
              value={newEmployee.last_name}
              onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
            />
            <input
              type="text"
              placeholder={t("col_address")}
              className="w-full p-2 border rounded mb-2"
              value={newEmployee.address}
              onChange={(e) => setNewEmployee({ ...newEmployee, address: e.target.value })}
            />
            <input
              type="email"
              placeholder={t("col_email")}
              className="w-full p-2 border rounded mb-2"
              value={newEmployee.contact_info.email}
              onChange={(e) =>
                setNewEmployee({ ...newEmployee, contact_info: { ...newEmployee.contact_info, email: e.target.value } })
              }
            />
            <input
              type="text"
              placeholder={t("col_phone")}
              className="w-full p-2 border rounded mb-2"
              value={newEmployee.contact_info.phone}
              onChange={(e) =>
                setNewEmployee({ ...newEmployee, contact_info: { ...newEmployee.contact_info, phone: e.target.value } })
              }
            />
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="newIsSalary"
                checked={newEmployee.is_salary}
                onChange={(e) => setNewEmployee({ ...newEmployee, is_salary: e.target.checked, salary_period: "weekly" })}
              />
              <label htmlFor="newIsSalary" className="ml-2">{t("is_salaried")}</label>
            </div>
            {newEmployee.is_salary && (
              <SalaryPeriodSelector
                value={newEmployee.salary_period}
                onChange={(p) => setNewEmployee({ ...newEmployee, salary_period: p })}
              />
            )}
            <input
              type="number"
              placeholder={wageLabel(newEmployee.is_salary, newEmployee.salary_period)}
              className="w-full p-2 border rounded mb-2"
              value={newEmployee.wage}
              onChange={(e) => setNewEmployee({ ...newEmployee, wage: e.target.value })}
            />

            <div className="flex justify-between mt-4">
              <button
                onClick={handleAddEmployee}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
              >
                {t("add_employee_title")}
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 transition"
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedEmployee && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">{t("edit_employee_title")}</h2>

            <input
              type="text"
              placeholder={t("col_first_name")}
              className="w-full p-2 border rounded mb-2"
              value={selectedEmployee.first_name || ""}
              onChange={(e) => setSelectedEmployee({ ...selectedEmployee, first_name: e.target.value })}
            />
            <input
              type="text"
              placeholder={t("col_last_name")}
              className="w-full p-2 border rounded mb-2"
              value={selectedEmployee.last_name || ""}
              onChange={(e) => setSelectedEmployee({ ...selectedEmployee, last_name: e.target.value })}
            />
            <input
              type="text"
              placeholder={t("col_address")}
              className="w-full p-2 border rounded mb-2"
              value={selectedEmployee.address || ""}
              onChange={(e) => setSelectedEmployee({ ...selectedEmployee, address: e.target.value })}
            />
            <input
              type="email"
              placeholder={t("col_email")}
              className="w-full p-2 border rounded mb-2"
              value={selectedEmployee.contact_info?.email || ""}
              onChange={(e) =>
                setSelectedEmployee({ ...selectedEmployee, contact_info: { ...selectedEmployee.contact_info, email: e.target.value } })
              }
            />
            <input
              type="text"
              placeholder={t("col_phone")}
              className="w-full p-2 border rounded mb-2"
              value={selectedEmployee.contact_info?.phone || ""}
              onChange={(e) =>
                setSelectedEmployee({ ...selectedEmployee, contact_info: { ...selectedEmployee.contact_info, phone: e.target.value } })
              }
            />
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="isSalary"
                checked={selectedEmployee.is_salary || false}
                onChange={(e) => setSelectedEmployee({ ...selectedEmployee, is_salary: e.target.checked, salary_period: "weekly" })}
              />
              <label htmlFor="isSalary" className="ml-2">{t("is_salaried")}</label>
            </div>
            {selectedEmployee.is_salary && (
              <SalaryPeriodSelector
                value={selectedEmployee.salary_period || "weekly"}
                onChange={(p) => setSelectedEmployee({ ...selectedEmployee, salary_period: p })}
              />
            )}
            <input
              type="number"
              placeholder={wageLabel(selectedEmployee.is_salary, selectedEmployee.salary_period)}
              className="w-full p-2 border rounded mb-2"
              value={selectedEmployee.wage || ""}
              onChange={(e) => setSelectedEmployee({ ...selectedEmployee, wage: e.target.value })}
            />

            <div className="flex justify-between mt-4">
              <button
                onClick={handleEditEmployee}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
              >
                {t("save_changes")}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 transition"
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      </Sidebar>
    </div>
  );
};

export default ManageEmployees;
